import { Archive, CornerDownRight, GitBranch, SkipForward } from 'lucide-react';
import { plainOptionLabel, type FactoryQuestionOption } from '@shared/factory-questions';
import { ChoiceCardGroup } from '@/components/ChoiceCard';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';

/** T-99: an item decided some other way than an answer: Skip, Backlog, or (wrap) New branch. */
export type DecisionChoice = 'skip' | 'backlog' | 'branch';

/** What's been picked for one item: options, the Other / steer text, and a choice in place of an answer. */
export interface DecisionValue {
  picked: string[];
  other: string;
  choice?: DecisionChoice | null;
}

/** The Other / steer box: one line high to start, growing with what's typed (several lines keep theirs, T-99). */
const BOX = 'min-h-9 py-1.5';

/** What each choice says once it's picked. */
const CHOSEN: Record<DecisionChoice, { label: string; icon: typeof SkipForward }> = {
  skip: { label: 'Skipped', icon: SkipForward },
  backlog: { label: 'Backlog', icon: Archive },
  branch: { label: 'New branch', icon: GitBranch },
};

/**
 * T-99: the controls on every item of the software factory's decisions (FactoryQuestionsCard before it
 * builds, FactorySuggestionsCard at the end of a turn), as on the factory's own cards: pick an option
 * (several with `multiSelect`), and/or write in the Other / steer box ("this, but …", or another way), or
 * decide it otherwise with Skip, Backlog or New branch (`actions`). Backlog and New branch keep the box
 * for a note for the new task, and Skip for why with `skipNote`. Choosing one keeps what was picked and
 * typed (the formats ignore the picks then), so Undo brings it all back. The box takes several lines.
 */
export function DecisionItemInput({
  label,
  options,
  multiSelect = false,
  recommended,
  value,
  onChange,
  actions = {},
  hints = {},
  placeholder,
  maxLength,
  skipNote = false,
}: {
  /** The item, read out for its controls ("Scope"). */
  label: string;
  options: FactoryQuestionOption[];
  multiSelect?: boolean;
  /** The recommended option's label: marked Recommended. */
  recommended?: string;
  value: DecisionValue | undefined;
  onChange: (change: Partial<DecisionValue>) => void;
  /** The choices offered besides an answer. */
  actions?: Partial<Record<DecisionChoice, boolean>>;
  /** What each choice does, as its hover text and the line once it's picked. */
  hints?: Partial<Record<DecisionChoice, string>>;
  /** The Other / steer box's placeholder. */
  placeholder?: string;
  /** The most characters the box takes. */
  maxLength?: number;
  /** Skip keeps the box, for why not (a suggestion's Skip note); without it, Skip carries nothing. */
  skipNote?: boolean;
}) {
  const picked = value?.picked ?? [];
  const choice = value?.choice ?? null;
  const isRecommended = (o: FactoryQuestionOption) => recommended !== undefined && o.label === recommended;
  const title = (o: FactoryQuestionOption) => plainOptionLabel(o.label);
  const offered = (Object.keys(CHOSEN) as DecisionChoice[]).filter((c) => actions[c]);

  if (choice) {
    const { label: chosen, icon: Icon } = CHOSEN[choice];
    return (
      <div className="space-y-1.5">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <CornerDownRight className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
          <Badge variant="secondary" size="sm">
            <Icon aria-hidden />
            {chosen}
          </Badge>
          {hints[choice] ? <span className="text-xs text-muted-foreground">{hints[choice]}</span> : null}
          <Button size="xs" variant="ghost" aria-label={`Undo ${chosen} on ${label}`} onClick={() => onChange({ choice: null })}>
            Undo
          </Button>
        </div>
        {choice !== 'skip' || skipNote ? (
          <Textarea
            aria-label={choice === 'skip' ? `${label}: why not` : `${label}: a note for the new task`}
            rows={1}
            className={BOX}
            placeholder={choice === 'skip' ? 'Why not? (optional)…' : 'Anything the new task should say (optional)…'}
            maxLength={maxLength}
            value={value?.other ?? ''}
            onChange={(e) => onChange({ other: e.target.value })}
          />
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {options.length && multiSelect ? (
        <div role="group" aria-label={label} className="space-y-1.5">
          {options.map((o) => (
            <label key={o.label} className="flex items-start gap-2 text-sm">
              <Checkbox
                className="mt-0.5"
                checked={picked.includes(o.label)}
                onCheckedChange={(on) => onChange({ picked: on === true ? [...picked, o.label] : picked.filter((p) => p !== o.label) })}
              />
              <span className="min-w-0">
                {title(o)} {isRecommended(o) ? <RecommendedBadge /> : null}
                {o.description ? <span className="block text-xs text-muted-foreground">{o.description}</span> : null}
              </span>
            </label>
          ))}
        </div>
      ) : options.length ? (
        <ChoiceCardGroup
          size="sm"
          label={label}
          value={picked[0] ?? ''}
          onValueChange={(v) => onChange({ picked: [v] })}
          options={options.map((o) => ({
            value: o.label,
            title: title(o),
            description: o.description,
            badge: isRecommended(o) ? <RecommendedBadge /> : undefined,
          }))}
        />
      ) : null}
      <Textarea
        aria-label={`${label}: ${options.length ? 'other, or steer the option picked' : 'your answer'}`}
        rows={1}
        className={BOX}
        placeholder={placeholder ?? (options.length ? 'Other, or steer it: “this, but …”' : 'Your answer…')}
        maxLength={maxLength}
        value={value?.other ?? ''}
        onChange={(e) => onChange({ other: e.target.value })}
      />
      {offered.length || picked.length ? (
        <div className="flex flex-wrap items-center gap-1">
          {offered.map((c) => {
            const { label: name, icon: Icon } = CHOSEN[c];
            const button = c === 'skip' ? 'Skip' : name;
            return (
              <Button
                key={c}
                size="xs"
                variant="outline"
                aria-label={`${button}: ${label}`}
                data-hint={hints[c]}
                // What's picked and typed stays, for Undo; the box is the note (Backlog, New branch, Skip with skipNote).
                onClick={() => onChange({ choice: c })}
              >
                <Icon aria-hidden />
                {button}
              </Button>
            );
          })}
          {picked.length ? (
            <Button size="xs" variant="link" className="text-muted-foreground" onClick={() => onChange({ picked: [] })}>
              Clear the pick
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function RecommendedBadge() {
  return (
    <Badge variant="ai" size="sm">
      Recommended
    </Badge>
  );
}
